import React from "react";
import * as helpers from "./portfolioHelpers";
import moment from "moment";
import { getBenchmarkName } from "../../helpers/nameHelpers";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  BarChart,
  Bar,
  Text,
} from "recharts";

const valueFormatter = (
  a,
  name = "",
  _,
  __,
  decimals = 2,
  percentDecimal = 2,
) => {
  if (_ && _.payload) {
    name = _.payload.name;
  }

  if (name.indexOf("Ending Value") >= 0) {
    return helpers.formatCell(a, "$");
  }
  if (name.indexOf("Portfolio Value") >= 0) {
    return helpers.formatCell(a * 1000, "$");
  } else if (name.indexOf("Beta") === 0) {
    return helpers.formatCell(a, "dec", decimals);
  } else if (name.indexOf("Return/Vol Adjusted Sharpe Ratio") === 0) {
    return helpers.formatCell(a, "dec", decimals);
  } else {
    return helpers.formatCell(a, "%", percentDecimal);
  }
};

const ChartItem = (props) => {
  const { title, subTitle, children, leftPadding = 60, style = {} } = props;

  return (
    <div
      style={{
        ...style,
        padding: 20,
        width: 600,
        border: "1px solid black",
        margin: 20,
      }}
    >
      {title && (
        <div style={{ width: "100%", display: "flex", textAlign: "center" }}>
          <div style={{ width: leftPadding }}></div>
          <div style={{ width: "100%" }}>
            {title && <div style={{ margin: 10, fontSize: 16 }}>{title}</div>}
            {subTitle && <div>{subTitle}</div>}
          </div>
        </div>
      )}
      <div>{children}</div>
    </div>
  );
};

const CustomizedAxisTick = (props, width = 100, style = { fontSize: 14 }) => {
  const { x, y, payload } = props;

  return (
    <Text
      x={x}
      y={y}
      width={width}
      textAnchor="middle"
      verticalAnchor="start"
      style={style}
    >
      {payload.value}
    </Text>
  );
};

export const GetChartByName = (props, chartName) => {
  const data = props.portfolio.charts;

  const currencyType = props.portfolio.portfolio.data.currencyType;

  const chartWidth = 600;
  const chartHeight = 300;

  const listLabelProps = {
    position: "top",
    fontSize: 14,
  };

  if (chartName === "portfolioValuesPer100ChartData")
    return (
      <ChartItem
        title={
          "Portfolio Values per $100 of Starting Date Value, Including Owner Contributions & Withdrawals After Respective Management Fees"
        }
      >
        <LineChart
          width={chartWidth}
          height={chartHeight}
          data={data.portfolioValuesPer100ChartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <XAxis
            interval={"preserveStart"}
            dataKey="date"
            dy={5}
            type="category"
            tickFormatter={(a) => {
              return moment(a).format("DD-MMM-YY");
            }}
          />
          <YAxis
            scale="linear"
            tickFormatter={(a) => {
              return "$" + a;
            }}
          />
          <CartesianGrid strokeDasharray="3 3" />
          <Tooltip
            labelFormatter={(a) => {
              return moment(a).format("MMM Do, YYYY");
            }}
            formatter={(data) => {
              return helpers.formatCell(data, "$");
            }}
          />
          <Legend
            wrapperStyle={{
              paddingTop: "20px",
            }}
          />
          <Line
            type="linear"
            name="Your Portfolio"
            dataKey="portfolio"
            stroke="black"
            strokeWidth={3}
            dot={false}
          />
          <Line
            type="linear"
            name="Your Benchmark"
            dataKey="benchmark"
            stroke="#413ea0"
            dot={false}
          />
          <Line
            type="linear"
            name={
              currencyType === "CAD" || !currencyType
                ? "TSX Composite"
                : "S&P 500"
            }
            dataKey="mainIndex"
            stroke="#ff7300"
            dot={false}
          />
          <Line
            type="linear"
            name="TBill"
            dataKey="tBill"
            stroke="#189038"
            dot={false}
          />
        </LineChart>
      </ChartItem>
    );

  function getLineItem() {}

  if (
    chartName === "benchmarkValuesPer100ChartData" &&
    data.benchmarkValuesPer100ChartData
  ) {
    var items = data.benchmarkValuesPer100ChartData[0].items;
    var itemNames = items.map((e) => e.name);

    var newList = [];

    data.benchmarkValuesPer100ChartData.forEach((item) => {
      var newItem = {
        date: item.date,
      };

      item.items.forEach((e) => {
        newItem[e.name] = e.value;
      });

      newList.push(newItem);
    });

    var colours = [
      "#8884d8",
      "#82ca9d",
      "#ff7300",
      "#189038",
      "#413ea0",
      "#00ff00",
      "#0000ff",
      "#ffff00",
      "#00ffff",
      "#ff00ff",
      "#008000",
      "#000080",
      "#808000",
      "#008080",
      "#9999ff",
    ];

    return (
      <ChartItem
        title={
          "Benchmark Values per $100 of Starting Date Value, Including Owner Contributions & Withdrawals After Respective Management Fees"
        }
      >
        <LineChart
          width={chartWidth}
          height={chartHeight}
          data={newList}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <XAxis
            interval={"preserveStart"}
            dataKey="date"
            dy={5}
            type="category"
            tickFormatter={(a) => {
              return moment(a).format("DD-MMM-YY");
            }}
          />
          <YAxis
            scale="linear"
            tickFormatter={(a) => {
              return "$" + a;
            }}
          />
          <CartesianGrid strokeDasharray="3 3" />
          <Tooltip
            labelFormatter={(a) => {
              return moment(a).format("MMM Do, YYYY");
            }}
            formatter={(value, name, props) => {
              var trimmedName = name;

              if (trimmedName.length > 25) {
                trimmedName = trimmedName.substring(0, 25).trim() + "...";
              }

              return [helpers.formatCell(value, "$"), trimmedName];
            }}
          />
          <Legend
            wrapperStyle={{
              paddingTop: "20px",
            }}
          />
          <Line
            type="linear"
            name="Your Benchmark"
            dataKey="clientSpecified"
            stroke="black"
            strokeWidth={3}
            dot={false}
          />

          {itemNames
            .filter((e) => e !== "clientSpecified")
            .map((e, i) => {
              return (
                <Line
                  key={i}
                  type="linear"
                  name={getBenchmarkName(e, currencyType)}
                  dataKey={e}
                  stroke={colours[i]}
                  dot={false}
                />
              );
            })}
        </LineChart>
      </ChartItem>
    );
  }

  var found;

  if (chartName === "comparativePortfolioAndBenchmarkReturns") {
    found = data.comparativePortfolioAndBenchmarkReturns.items.find(
      (e) => e.value1 < 0 || e.value2 < 0,
    );

    return (
      <ChartItem
        title={"Comparative Portfolio & Benchmark Returns, After Fees, %/year"}
        subTitle={`${moment(data.comparativePortfolioAndBenchmarkReturns.startDate).format("DD-MMM-YY")} to ${moment(data.comparativePortfolioAndBenchmarkReturns.endDate).format("DD-MMM-YY")}`}
      >
        <BarChart
          width={chartWidth}
          height={chartHeight}
          data={data.comparativePortfolioAndBenchmarkReturns.items}
          margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
        >
          <XAxis dataKey="name" tick={(p) => CustomizedAxisTick(p)} />
          <YAxis
            width={40}
            tick={false}
            axisLine={false}
            padding={{ bottom: found ? 15 : 0 }}
            label={{
              value: "Annualized Rates of Return",
              angle: -90,
              position: "center",
              width: 300,
            }}
          />
          <Tooltip />
          <Legend
            wrapperStyle={{
              paddingTop: "20px",
            }}
          />
          <Bar
            dataKey="value1"
            name={"Internal Rate of Return, IRR"}
            fill="#5350a2"
            formatter={valueFormatter}
          >
            <LabelList
              {...listLabelProps}
              dataKey="value1"
              formatter={valueFormatter}
            />
          </Bar>
          <Bar
            dataKey="value2"
            name={"Holding Period Return, HPR"}
            fill="#3aa558"
            formatter={valueFormatter}
          >
            <LabelList {...listLabelProps} formatter={valueFormatter} />
          </Bar>
        </BarChart>
      </ChartItem>
    );
  }
  if (chartName === "comparativePortfolioAndBenchmarkRisks") {
    found = data.comparativePortfolioAndBenchmarkRisks.items.find(
      (e) => e.value1 < 0 || e.value2 < 0,
    );

    return (
      <ChartItem
        leftPadding={0}
        title={
          "Comparative Portfolio & Benchmark Risks & Reward After Fees, /month"
        }
        subTitle={`${moment(data.comparativePortfolioAndBenchmarkRisks.startDate).format("DD-MMM-YY")} to ${moment(data.comparativePortfolioAndBenchmarkReturns.endDate).format("DD-MMM-YY")}`}
      >
        <BarChart
          width={chartWidth}
          height={chartHeight}
          data={data.comparativePortfolioAndBenchmarkRisks.items}
          margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
        >
          <XAxis
            dataKey="name"
            interval={0}
            allowDataOverflow={false}
            tick={(p) => CustomizedAxisTick(p, 150)}
          />
          <YAxis hide={true} padding={{ bottom: found ? 15 : 0 }} />
          <Tooltip />
          <Legend
            wrapperStyle={{
              paddingTop: "20px",
            }}
          />
          <Bar
            dataKey="value1"
            name={"Your Portfolio"}
            fill="#8884d8"
            formatter={valueFormatter}
          >
            <LabelList
              {...listLabelProps}
              valueAccessor={(a) => {
                return valueFormatter(a.value1, a.name, null, null, 2, 1);
              }}
            />
          </Bar>
          <Bar
            dataKey="value2"
            name={"Your Benchmark"}
            fill="#82ca9d"
            formatter={valueFormatter}
          >
            <LabelList
              {...listLabelProps}
              valueAccessor={(a) => {
                return valueFormatter(a.value2, a.name, null, null, 2, 1);
              }}
            />
          </Bar>
          <Bar
            dataKey="value3"
            name={"Combined Portfolio & Benchmark"}
            fill="#e6cf6f"
            formatter={valueFormatter}
          >
            <LabelList
              {...listLabelProps}
              valueAccessor={(a) => {
                return valueFormatter(a.value3, a.name);
              }}
            />
          </Bar>
        </BarChart>
      </ChartItem>
    );
  }

  if (chartName === "comparativeAverageReturn") {
    found = data.comparativeAverageReturn.items.find(
      (e) => e.value1 < 0 || e.value2 < 0,
    );

    return (
      <ChartItem
        leftPadding={0}
        title={
          "Comparative Average Return, Ending Value, Volatility Risk, & Beta Risk Added by Management Relative to the Benchmark, after fees"
        }
        subTitle={`${moment(data.comparativeAverageReturn.startDate).format("DD-MMM-YY")} to ${moment(data.comparativeAverageReturn.endDate).format("DD-MMM-YY")}`}
      >
        <BarChart
          width={chartWidth}
          height={chartHeight}
          data={data.comparativeAverageReturn.items}
          margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
        >
          <XAxis
            interval={0}
            dataKey="name"
            allowDataOverflow={false}
            tick={(p) => CustomizedAxisTick(p, 150)}
          />
          <YAxis padding={{ bottom: found ? 15 : 0 }} hide={true} />
          <Tooltip />
          {/* <Legend wrapperStyle={{
                    paddingTop: '20px'
                }} /> */}
          <Bar
            dataKey="value1"
            name={"After Fees"}
            fill="#ffa500"
            formatter={valueFormatter}
          >
            <LabelList
              {...listLabelProps}
              valueAccessor={(a) => {
                return valueFormatter(a.value1, a.name);
              }}
            />
          </Bar>
          {/* <Bar dataKey="value2" name={"Before Fees"} fill="#003eff" formatter={valueFormatter}>
                    <LabelList {...listLabelProps} valueAccessor={(a) => {
                        return valueFormatter(a.value2, a.name);
                    }} /> */}
          {/* </Bar> */}
        </BarChart>
      </ChartItem>
    );
  }

  if (chartName === "maximumRecommendedManagerFees") {
    found = data.maximumRecommendedManagerFees.items.find(
      (e) => e.value1 < 0 || e.value2 < 0,
    );

    return (
      <ChartItem
        title={
          "Maximum Recommended Manager Fees Compared to the Manager & Benchmark Actual Fees, annualized"
        }
        subTitle={`${moment(data.maximumRecommendedManagerFees.startDate).format("DD-MMM-YY")} to ${moment(data.maximumRecommendedManagerFees.endDate).format("DD-MMM-YY")}`}
      >
        <BarChart
          width={chartWidth}
          height={chartHeight}
          data={data.maximumRecommendedManagerFees.items}
          margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
        >
          <XAxis dataKey="name" tick={(p) => CustomizedAxisTick(p)} />
          <YAxis
            padding={{ bottom: found ? 15 : 0 }}
            width={40}
            tick={false}
            axisLine={false}
            label={{
              value: "Manager Fees, %/year",
              angle: -90,
              position: "center",
              width: 300,
            }}
          />
          <Tooltip />
          <Bar
            dataKey="value1"
            fill="#8884d8"
            name={"Manager Fees, %/year"}
            formatter={valueFormatter}
          >
            <LabelList {...listLabelProps} formatter={valueFormatter} />
          </Bar>
        </BarChart>
      </ChartItem>
    );
  }

  if (chartName === "portfoliosSensitivityToFactorsAfterFees") {
    found = data.portfoliosSensitivityToFactorsAfterFees.items.find(
      (e) => e.value1 < 0 || e.value2 < 0,
    );

    return (
      <ChartItem
        title="Portfolio's Exposures to Economic Factors, per month"
        subTitle={`${moment(data.portfoliosSensitivityToFactorsAfterFees.startDate).format("DD-MMM-YY")} to ${moment(data.portfoliosSensitivityToFactorsAfterFees.endDate).format("DD-MMM-YY")}`}
      >
        <BarChart
          width={chartWidth}
          height={chartHeight}
          data={data.portfoliosSensitivityToFactorsAfterFees.items}
          margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
        >
          <XAxis
            interval={0}
            dataKey="name"
            allowDataOverflow={false}
            height={40}
            tick={(p) => CustomizedAxisTick(p, 70, { fontSize: 12 })}
          />
          <YAxis
            padding={{ bottom: found ? 15 : 0 }}
            width={40}
            tick={false}
            axisLine={false}
            label={{
              value: "Your Portfolio's Sensitivities to the Factors",
              angle: -90,
              position: "center",
              width: 350,
              fontSize: 12,
            }}
          />
          <Tooltip />
          <Bar
            dataKey="value1"
            fill="#8884d8"
            name="Sensitivities to the Factors"
            formatter={(a) => {
              return helpers.formatCell(a, "dec", 2);
            }}
          >
            <LabelList
              {...listLabelProps}
              valueAccessor={(a) => {
                return helpers.formatCell(a.value1, "dec", 2);
              }}
            />
          </Bar>
        </BarChart>
      </ChartItem>
    );
  }

  return <div>Chart not found</div>;
};

const Charts = () => {
  return <div style={{ fontSize: 14 }}></div>;
};

export default Charts;



// WEBPACK FOOTER //
// ./src/portfolio/components/Charts.js